import { useState, useCallback } from "react";
import { AttributeValueItem } from "@/lib/constants";
import { Offers, Review } from "next-seo/lib/types";
import { event } from 'nextjs-google-analytics';

export interface UseProductAttributesResult {
  attributeValues: any;
  attributeSelections: any;
  updateAttributeSelection: (key: string, value: number) => void;
  updateAttributeValues: (key: string, values: ReadonlyArray<AttributeValueItem>) => void;
  additionalImages: string[];
  offerVariants: Offers[];
  reviewList: Review[];
  parseAttributes: () => Promise<void>;
}

export function useProductAttributes(props: any, attributesData: any) {
  const [attributeSelections, setAttributeSelections] = useState({
    color: 0,
    size: 0,
    stage: 0,
    pattern: 0,
    gender: 0,
    flavour: 0
  });
  const [attributeValues, setAttributeValues] = useState<{
    colors: AttributeValueItem[];
    sizes: AttributeValueItem[];
    stages: AttributeValueItem[];
    patterns: AttributeValueItem[];
    genders: AttributeValueItem[];
    flavours: AttributeValueItem[];
  }>({
    colors: [],
    sizes: [],
    stages: [],
    patterns: [],
    genders: [],
    flavours: []
  });
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [offerVariants, setOfferVariants] = useState<Offers[]>([]);
  const [reviewList, setReviewList] = useState<Review[]>([]);

  const updateAttributeSelection = useCallback((key: string, value: number) => {
    setAttributeSelections((prev: any) => ({ ...prev, [key]: value }));
  }, []);

  const updateAttributeValues = useCallback((key: string, values: ReadonlyArray<AttributeValueItem>) => {
    setAttributeValues((prev: any) => ({ ...prev, [key]: values }));
  }, []);

  const parseAttributes = useCallback(async () => {
    setAdditionalImages([]);
    setOfferVariants([]);
    setReviewList([]);
    const priceValidDate = new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000);
    const priceValidUntil = `${priceValidDate.getFullYear()}-${(priceValidDate.getMonth() + 1).toString().padStart(2, '0')}-${priceValidDate.getDate().toString().padStart(2, '0')}`;
    try {
      for (const attr of attributesData.data) {
        const filtered = attr.attribute_values
          .filter((attribute: any) => props.attribute_value_list.some((value: any) => attribute.id === value))
          .sort((a: any, b: any) => (a.order === b.order) ? a.id - b.id : a.order - b.order);
        switch (attr.slug) {
          case "color":
            updateAttributeValues("colors", filtered);
            if (filtered.length > 0) updateAttributeSelection("color", filtered[0].id);
            break;
          case "size":
            updateAttributeValues("sizes", filtered);
            if (filtered.length > 0) updateAttributeSelection("size", filtered[0].id);
            break;
          case "stage":
            updateAttributeValues("stages", filtered);
            if (filtered.length > 0) updateAttributeSelection("stage", filtered[0].id);
            break;
          case "pattern":
            updateAttributeValues("patterns", filtered);
            if (filtered.length > 0) updateAttributeSelection("pattern", filtered[0].id);
            break;
          case "gender":
            updateAttributeValues("genders", filtered);
            if (filtered.length > 0) updateAttributeSelection("gender", filtered[0].id);
            break;
          case "flavour":
            updateAttributeValues("flavours", filtered);
            if (filtered.length > 0) updateAttributeSelection("flavour", filtered[0].id);
            break;
        }
      }
      props.image_gallery.slice(1).forEach((image: any) => {
        if (image?.image) {
          setAdditionalImages((prev) => [...prev, image.image]);
        }
      });
      if (props.product_variant_list.length === 0) {
        if (["archive", "stop_selling"].includes(props.sales_status)) {
          setOfferVariants([{
            price: "0",
            priceCurrency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'IDR',
            url: `${process.env.NEXT_PUBLIC_HOSTNAME}/product/${props.slug}`,
            availability: "https://schema.org/Discontinued",
            itemCondition: "https://schema.org/NewCondition",
            seller: { name: "MOOIMOM" }
          }]);
        } else {
          setOfferVariants([{
            price: "0",
            priceCurrency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'IDR',
            url: `${process.env.NEXT_PUBLIC_HOSTNAME}/product/${props.slug}`,
            availability: "https://schema.org/PreOrder",
            itemCondition: "https://schema.org/NewCondition",
            seller: { name: "MOOIMOM" }
          }]);
        }
      } else {
        props.product_variant_list.forEach((v: any) => {
          const color = attributeValues.colors.find((c) => v.attribute_value_list.includes(c.id)) as AttributeValueItem | undefined;
          const size = attributeValues.sizes.find((s) => v.attribute_value_list.includes(s.id)) as AttributeValueItem | undefined;
          const stage = attributeValues.stages.find((s) => v.attribute_value_list.includes(s.id)) as AttributeValueItem | undefined;
          const pattern = attributeValues.patterns.find((p) => v.attribute_value_list.includes(p.id)) as AttributeValueItem | undefined;
          const gender = attributeValues.genders.find((g) => v.attribute_value_list.includes(g.id)) as AttributeValueItem | undefined;
          const flavour = attributeValues.flavours.find((f) => v.attribute_value_list.includes(f.id)) as AttributeValueItem | undefined;
          const ov: any = {
            price: (v.special_price > 0 ? v.special_price : v.sales_price).toString(),
            priceValidUntil,
            priceCurrency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'IDR',
            url: `${process.env.NEXT_PUBLIC_HOSTNAME}/product/${props.slug}`,
            availability: v.qty > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { name: "MOOIMOM" },
            name: props.name,
            sku: v.product_code
          };
          if (color && (color.base_color || color.name)) {
            ov.color = color.base_color || color.name;
          }
          if (size && size.name) {
            ov.size = size.name;
          }
          if (stage && stage.name) {
            ov.stage = stage.name;
          }
          if (pattern && pattern.name) {
            ov.pattern = pattern.name;
          }
          if (gender && gender.name) {
            ov.gender = gender.name;
          }
          if (flavour && flavour.name) {
            ov.flavour = flavour.name;
          }
          setOfferVariants((prev) => [...prev, ov]);
        });
      }
      if (props.rating?.review_list?.length > 0) {
        props.rating.review_list.forEach((review: any) => {
          setReviewList((prev) => [...prev, {
            author: review.customer_name,
            datePublished: review.review_date,
            reviewBody: review.content,
            name: review.title,
            reviewRating: {
              bestRating: props.rating.best_rating.toString(),
              ratingValue: review.star_value.toString(),
              worstRating: props.rating.worst_rating.toString()
            }
          }]);
        });
      }
      event("view_item", {
        category: "ecommerce",
        currency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'IDR',
        value: props.sales_price * props.qty,
        items: [{
          item_id: props.product_code,
          item_name: props.name,
          brand: props.brand
        }]
      });
      try {
        window.fbq('track', 'viewContent', {
          content_ids: [props.product_code],
          content_name: props.name,
          content_type: 'product',
          value: props.sales_price * props.qty,
          currency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'IDR'
        });
      } catch (error) {
        console.error('Facebook Pixel Error:', error);
      }
    } catch (error) {
      console.error('Error parsing attributes:', error);
    }
  }, [props, attributesData, attributeValues, updateAttributeValues, updateAttributeSelection]);

  return {
    attributeValues,
    attributeSelections,
    updateAttributeSelection,
    updateAttributeValues,
    additionalImages,
    offerVariants,
    reviewList,
    parseAttributes
  };
}
